// Client-side mirror of the per-role write rules enforced by the CouchDB
// validate_doc_update design doc (infra/couchdb/design/access.js).
//
// PouchDB runs the replicated local validator with an empty userCtx
// (roles=[]), so the role branches in access.js never fire offline: a
// role-forbidden write is accepted into the local DB and only bounces when it
// reaches the server on push. This reproduces the same role→type matrix on the
// client so a forbidden write is blocked before it enters the local DB /
// replication, keeping offline behavior faithful to the server.
//
// Keep in lockstep with access.js `validateDataDocUpdate`.

const ALL_TYPES = [
  'customer',
  'extinguisher',
  'hose',
  'visit',
  'archive',
  'worker',
  'invoice',
  'audit',
  'manufacturer',
  'device_type',
  'spare_part',
  'service_price',
  'hose_config',
  'settings',
  'inspection',
  'photo_ref',
  'signature',
  'report',
  'report_request',
  'invoice_email_pending',
  'refresh_token',
  'password_reset',
];

const TECH_TYPES = [
  'customer',
  'extinguisher',
  'hose',
  'visit',
  'archive',
  'inspection',
  'photo_ref',
  'signature',
];

const STAFF_FORBIDDEN = ['service_price', 'worker', 'settings'];

// Mirrors access.js: super_admin alone must impersonate; technicians may write
// only field/service entities; staff may write all but price/worker/settings;
// org_admin (or any unrecognized role, matching the server fall-through) may
// write any valid type. `_admin` bypasses everything.
export function canWrite(roles: string[], type: string): boolean {
  if (roles.indexOf('_admin') !== -1) return true;
  if (ALL_TYPES.indexOf(type) === -1) return false;

  const isSuper = roles.indexOf('super_admin') !== -1;
  const isOrgAdmin = roles.indexOf('org_admin') !== -1;
  const isStaff = roles.indexOf('staff') !== -1;
  const isTech = roles.indexOf('technician') !== -1;

  if (isSuper && !isOrgAdmin && !isStaff && !isTech) return false;

  if (isTech && !isOrgAdmin && !isStaff) {
    return TECH_TYPES.indexOf(type) !== -1;
  }
  if (isStaff && !isOrgAdmin) {
    return STAFF_FORBIDDEN.indexOf(type) === -1;
  }
  return true;
}
